import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';

import {
  Affinity,
  ChatVisibility,
  PartnershipCard,
  User,
  UserIdentification,
  UsersId,
  ValidationResponse,
} from '@models/swagger';
import { ApiService, DefaultHttpHeaders } from '@services/api/api.service';
import { catchError, finalize, retry, tap } from 'rxjs/operators';
import { GenesysChatVisibilityInd } from '@store/flow-data/general';

@Injectable({
  providedIn: 'root',
})
export class ApplicationUtilitiesService extends ApiService {
  user: User | undefined;
  private readonly _getUserByIdLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly getUserByIdLoading$ = this._getUserByIdLoading$.asObservable();
  getUserById(params: GetUserByIdParams): Observable<User> {
    const pathParams = {
      userId: params.userId,
    };
    this._getUserByIdLoading$.next(true);
    return this.get<User>({
      suffix: `/applicationUtilities/user/${pathParams.userId}`,
      authenticationType: 'jwt',
    }).pipe(
      tap(response => (this.user = response)),
      retry(1),
      finalize(() => this._getUserByIdLoading$.next(false))
    );
  }

  private readonly _usersIdLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly usersIdLoading$ = this._usersIdLoading$.asObservable();
  usersId(body: UsersId): Observable<UserIdentification> {
    this._usersIdLoading$.next(true);
    return this.post<UsersId, UserIdentification>({
      suffix: '/comeround/applicationUtilities/users/id',
      body,
    }).pipe(
      retry(1),
      finalize(() => this._usersIdLoading$.next(false))
    );
  }

  private readonly _validatePartnershipCardLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly validatePartnershipCardLoading$ = this._validatePartnershipCardLoading$.asObservable();
  validatePartnershipCard(body: PartnershipCard): Observable<ValidationResponse> {
    this._validatePartnershipCardLoading$.next(true);
    return this.post<PartnershipCard, ValidationResponse>({
      suffix: '/comeround/applicationUtilities/validatePartnershipCard',
      body,
    }).pipe(
      retry(1),
      finalize(() => this._validatePartnershipCardLoading$.next(false))
    );
  }

  private readonly _getChatVisibilityLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly getChatVisibility$ = this._getChatVisibilityLoading$.asObservable();
  getChatVisibility({
    customerType = 'self',
    requestType,
  }: {
    customerType?: 'self' | 'portal';
    requestType: 'partial' | 'complete';
  }): Observable<ChatVisibility> {
    return this.get<ChatVisibility>({
      suffix: `/applicationUtilities/chatVisibility`,
      queryParams: {
        customerType,
        requestType,
      },
    }).pipe(
      tap((response: ChatVisibility) => {
        const status = response.chatVisibilityStatus;
        const agentsAvailable = status !== undefined && (status === 'AGENTS_AVAILABLE' || status === 'AVAILABLE');
        this._getChatVisibilityLoading$.next(agentsAvailable);
      }),
      catchError(error => {
        this._getChatVisibilityLoading$.next(false);
        return throwError(() => error);
      })
    );
  }

  private readonly _getAffinityLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly getAffinityLoading$ = this._getAffinityLoading$.asObservable();
  getAffinity(queryParams: AffinityParameters): Observable<Affinity> {
    this._getAffinityLoading$.next(true);
    return this.get<Affinity>({
      suffix: `/comeround/applicationUtilities/affinity/${queryParams.alliance}/${queryParams.clientType}`,
    }).pipe(finalize(() => this._getAffinityLoading$.next(false)));
  }
}

interface GetUserByIdParams extends DefaultHttpHeaders {
  userId: string;
}

interface AffinityParameters extends DefaultHttpHeaders {
  alliance: string;
  clientType: string;
}
